'use client'
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react'
import { imageUrl } from './products'
import ManufacturerSizeChart from './ManufacturerSizeChart'
import { HandwrittenMoney } from './Handwriting'
import PhotoMagnifier from './PhotoMagnifier'
import { SHIRT_COLORS, SHIRT_SIZES, ShirtColor, ShirtSize, ShirtSelection, shirtImage, shirtVariant } from './apparel'

const description = "Hi. This is Aadil. I'm writing this in some corporate swag notebook from a cafe in New York right now. Hope it still gets the msg across. I made the initial shirts for $100. Stupid, I know, but I just wanted something fast to show off that I would love wearing. The blank was $30, embroidery was $50 (criminal! but I get it, I tried using a machine at home and it sucks), and rush fee was $20. But if a bunch of people order, I can halve all those costs and nix the rush fee so it'll cost $40 + $10 shipped. If not, I'll refund, but I make no profit. I just think it's cool, and see how much I/others resonate w this msg."
const hand = (name: string) => imageUrl(`hand/shirt-hand-${name}.webp`)
const modelScale: Record<ShirtSize, number> = { S: .78, M: 1, L: 1.18, XL: 1.42 }

// Keep the original ink at its original scale. Follow the photographed rules
// so neither the greeting nor the final line's tall letters are clipped.
const noteTopEdge = [[0,270],[100,270],[200,270],[300,272],[400,276],[500,282],[600,287],[700,291],[800,294],[900,297],[1000,299],[1100,301],[1200,301],[1300,302],[1400,304],[1408,304],[1408,326],[1425,326],[1425,304],[1500,304],[1600,305],[1700,304],[1800,301],[1900,297],[2000,292],[2100,287],[2200,280]]
const noteLastLineEdge = [[0,3287],[100,3291],[200,3295],[300,3299],[400,3298],[500,3297],[600,3294],[700,3290],[800,3285],[900,3280],[1000,3275],[1100,3270],[1200,3267],[1300,3262],[1400,3258],[1438,3258],[1438,3262],[1450,3262],[1450,3256],[1500,3254],[1600,3251],[1700,3248],[1800,3247],[1900,3249],[2000,3252],[2100,3258],[2200,3259]]
const noteBlankRule = [[0,3038],[100,3042],[200,3046],[300,3049],[400,3050],[500,3048],[600,3046],[700,3043],[800,3040],[900,3036],[1000,3032],[1100,3028],[1200,3024],[1300,3020],[1400,3018],[1500,3014],[1600,3013],[1700,3012],[1800,3013],[1900,3015],[2000,3019],[2100,3024],[2200,3028]]
const notePolygon = (points: number[][]) => points.map(point => point.join(',')).join(' ')

function ShirtDescriptionNote() {
  const id = useId()
  const source = hand('description')
  return <svg viewBox="0 260 2200 3310" width={2200} height={3310}
    style={{ display: 'block', width: '100%', height: 'auto' }}
    role="img" aria-label={description}>
    <defs>
      <clipPath id={`${id}-body`}>
        <polygon points={notePolygon([...noteTopEdge, ...noteLastLineEdge.slice().reverse()])}/>
      </clipPath>
      <clipPath id={`${id}-last`}>
        <polygon points={notePolygon([...noteLastLineEdge, [2200,3450], [0,3450]])}/>
      </clipPath>
      <clipPath id={`${id}-blank-rule`}>
        <polygon points={notePolygon([...noteBlankRule.map(([x,y]) => [x,y-4]), ...noteBlankRule.slice().reverse().map(([x,y]) => [x,y+4])])}/>
      </clipPath>
      <clipPath id={`${id}-top-rule`}><rect x={1435} y={306} width={17} height={9}/></clipPath>
    </defs>
    <image href={source} width={2200} height={3450} clipPath={`url(#${id}-body)`}/>
    {/* Remove the URL's descending p and bridge its tiny gap with adjacent original rule ink. */}
    <g transform="translate(-27 0)">
      <image href={source} width={2200} height={3450} clipPath={`url(#${id}-top-rule)`}/>
    </g>
    {/* Reuse a clean original rule; a slight shear follows the photographed page. */}
    <g transform="matrix(1 -.006 0 1 0 360)">
      <image href={source} width={2200} height={3450} clipPath={`url(#${id}-blank-rule)`}/>
    </g>
    {/* One original notebook row separates the final two text lines. */}
    <g transform="translate(0 120)">
      <image href={source} width={2200} height={3450} clipPath={`url(#${id}-last)`}/>
    </g>
  </svg>
}

export default function ShirtProduct({ onAdd, visible = true, leaving = false }: { onAdd: (selection: ShirtSelection) => void; visible?: boolean; leaving?: boolean }) {
  const [color, setColor] = useState<ShirtColor>('Black')
  const [size, setSize] = useState<ShirtSize | null>(null)
  const [detail, setDetail] = useState(true)
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => { if (visible) heading.current?.focus({ preventScroll: true }) }, [visible])
  const variant = size ? shirtVariant(color) : undefined
  return <section className={`shirt-page${visible ? '' : leaving ? ' is-leaving' : ' is-entering'}`} aria-label="T-shirt product details" aria-hidden={!visible} inert={!visible}>
    <div className="shirt-layout" style={{ '--model-scale': detail ? 1 : modelScale[size ?? 'M'] } as CSSProperties}>
      <div className="shirt-gallery">
        <PhotoMagnifier key={detail ? 'detail' : color} src={imageUrl(detail ? 'shirt/idea-muscle-detail.webp' : shirtImage(color))}
          alt={detail ? 'Black and white shirts with tonal Training the Idea Muscle embroidery' : `${color} Training the Idea Muscle T-shirt, worn by the model${color === 'White' ? ' smiling' : ''}`} />
      </div>
      <div className="shirt-details">
        <h1 ref={heading} tabIndex={-1} className="shirt-title">
          <span className="sr-only">Training the Idea Muscle T-shirt</span>
          <span aria-hidden="true"><img src={imageUrl('hand/word-riley-training.svg')} alt=""/><img src={imageUrl('hand/word-riley-the.svg')} alt=""/><br/><img src={imageUrl('hand/word-riley-idea.svg')} alt=""/><img src={imageUrl('hand/word-riley-muscle.svg')} alt=""/></span>
        </h1>
        <p className="shirt-caption"><img src={hand('embroidered-tee')} alt="Embroidered Tee" width={1570} height={435}/></p>
        <div className="shirt-price"><HandwrittenMoney value={50} hideZeroCents/></div>
        <fieldset className="shirt-options">
          <legend className="sr-only">Color</legend>
          <div className="shirt-color-options">{SHIRT_COLORS.map(c => <button key={c} type="button" aria-pressed={color === c}
            aria-label={c} className="shirt-color" onClick={() => { setColor(c); setDetail(false) }}>
            <span style={{ background: c === 'Black' ? '#202020' : '#f7f5ee' }} /><img src={hand(c.toLowerCase())} alt=""/>
          </button>)}</div>
        </fieldset>
        <fieldset className="shirt-options">
          <legend className="sr-only">Size</legend>
          <div className="shirt-size-options">{SHIRT_SIZES.map(s => <button key={s} type="button" aria-label={s} aria-pressed={size === s} onClick={() => { setSize(s); setDetail(false) }}><img src={hand(`size-${s.toLowerCase()}`)} alt=""/></button>)}</div>
        </fieldset>
        <button className="shop-add-to-cart shirt-add-to-cart" aria-label="Add to cart" disabled={!variant} onClick={() => size && onAdd({color, size})}>
          <img className="shop-cart-base" src={imageUrl('hand/cart-button.svg')} width={605} height={166} alt="" draggable={false}/>
          <img className="shop-cart-face" src={imageUrl('hand/cart-button.svg')} width={605} height={166} alt="" draggable={false}/>
        </button>
        <p className="shirt-shipping-note">Inventory pending. Ships in 8-12 weeks</p>
        <details className="shirt-disclosure">
          <summary><img src={hand('size-guide')} alt="Size Guide" width={1133} height={244}/><span>+</span></summary>
          <ManufacturerSizeChart/>
        </details>
        <div className="shirt-description-note">
          <span className="shirt-clear-tape" aria-hidden="true"/>
          <ShirtDescriptionNote/>
        </div>
      </div>
    </div>
  </section>
}
